// Decode HTML character references in scraped text: `&quot;` -> `"`,
// `&#064;` -> `@`, `&#x2022;` -> `•`. Meta tags and embed markup carry them
// raw, and a regex scrape keeps them, so titles came out as
// `BEHIND THE BRIEF on Instagram: &quot;You know that…`.
//
// Pure TypeScript with no imports: used by the fetch-link-metadata edge
// function (Deno) and by the app (src/lib/htmlEntities.ts) alike.

const NAMED: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  hellip: '…', mdash: '—', ndash: '–', bull: '•', middot: '·',
  lsquo: '‘', rsquo: '’', sbquo: '‚', ldquo: '“', rdquo: '”', bdquo: '„',
  laquo: '«', raquo: '»', lsaquo: '‹', rsaquo: '›',
  copy: '©', reg: '®', trade: '™', deg: '°', times: '×', divide: '÷',
  euro: '€', pound: '£', yen: '¥', cent: '¢', sect: '§', para: '¶',
  iexcl: '¡', iquest: '¿', shy: '­', zwj: '‍', zwnj: '‌',
  aacute: 'á', eacute: 'é', iacute: 'í', oacute: 'ó', uacute: 'ú', yacute: 'ý',
  Aacute: 'Á', Eacute: 'É', Iacute: 'Í', Oacute: 'Ó', Uacute: 'Ú',
  agrave: 'à', egrave: 'è', igrave: 'ì', ograve: 'ò', ugrave: 'ù',
  acirc: 'â', ecirc: 'ê', icirc: 'î', ocirc: 'ô', ucirc: 'û',
  auml: 'ä', euml: 'ë', iuml: 'ï', ouml: 'ö', uuml: 'ü', yuml: 'ÿ',
  Auml: 'Ä', Ouml: 'Ö', Uuml: 'Ü', szlig: 'ß',
  atilde: 'ã', ntilde: 'ñ', otilde: 'õ', Ntilde: 'Ñ', ccedil: 'ç', Ccedil: 'Ç',
  aring: 'å', aelig: 'æ', oslash: 'ø', Aring: 'Å', AElig: 'Æ', Oslash: 'Ø',
};

const REFERENCE = /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi;

function decodeOnce(text: string): string {
  return text.replace(REFERENCE, (match, ref: string) => {
    if (ref[0] === '#') {
      const code = ref[1] === 'x' || ref[1] === 'X' ? parseInt(ref.slice(2), 16) : parseInt(ref.slice(1), 10);
      // Lone surrogates and out-of-range values stay as written
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return match;
      return String.fromCodePoint(code);
    }
    return NAMED[ref] ?? NAMED[ref.toLowerCase()] ?? match;
  });
}

/**
 * Decode named and numeric character references. Also undoes one level of
 * double encoding (`&amp;quot;`), which some sites send in og tags. Unknown
 * names are left as written.
 */
export function decodeHtmlEntities(text: string): string;
export function decodeHtmlEntities(text: string | null | undefined): string | null | undefined;
export function decodeHtmlEntities(text: string | null | undefined): string | null | undefined {
  if (!text || !text.includes('&')) return text;
  const once = decodeOnce(text);
  return /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/i.test(once) && /&amp;/i.test(text) ? decodeOnce(once) : once;
}
